import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../../api";
import { SectionTitle, EmptyState, Avatar, StatusPill } from "../../components/Atoms";
import { Megaphone, Link as LinkIcon, FileArrowDown, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const TABS = ["Pending", "Approved", "Rejected"];

export default function ClientSubmissionsPage() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("Pending");
  const [issueOpen, setIssueOpen] = useState(null);
  const [issue, setIssue] = useState({ subject:"", description:"" });

  const load = useCallback(async () => {
    const { data } = await api.get(`/submissions?status=${tab}`);
    setItems(data);
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const raise = async (e) => {
    e.preventDefault();
    try {
      await api.post("/issues", { ...issue, submission_id: issueOpen.id, ambassador_id: issueOpen.ambassador_id, project_id: issueOpen.project_id });
      toast.success("Issue raised — admin will review.");
      setIssueOpen(null); setIssue({ subject:"", description:"" });
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-5">
      <SectionTitle title="Submissions" subtitle="Creator deliverables for your campaigns" />
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-full text-sm font-bold ${tab === t ? "bg-white/10 text-white border border-white/15" : "text-muted bg-white/5"}`}>{t}</button>
        ))}
      </div>
      {items.length === 0 ? <EmptyState icon={Megaphone} /> : (
        <div className="grid lg:grid-cols-2 gap-4">
          {items.map(s => (
            <div key={s.id} className="glass p-4 hover-lift">
              <Link to={`/client/ambassadors/${s.ambassador_id}`} className="flex items-center gap-3 hover:opacity-80" data-testid={`client-sub-amb-${s.id}`}>
                <Avatar src={s.ambassador_photo} name={s.ambassador_name} />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold truncate hover:text-[#22D3EE]">{s.task_title}</div>
                  <div className="text-xs text-muted truncate">{s.ambassador_name} · {s.project_title}</div>
                </div>
                <StatusPill status={s.status} />
              </Link>
              {s.notes && <div className="text-sm text-muted mt-3">{s.notes}</div>}
              <div className="flex flex-wrap gap-2 mt-3">
                {s.link && <a href={s.link} target="_blank" rel="noreferrer" className="btn-ghost text-xs flex items-center gap-1.5"><LinkIcon size={14} /> Open link</a>}
                {s.file_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${s.file_url}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs flex items-center gap-1.5"><FileArrowDown size={14} /> File</a>}
                <button onClick={() => setIssueOpen(s)} className="btn-ghost text-xs text-[#F59E0B] flex items-center gap-1.5"><WarningCircle size={14} /> Raise issue</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {issueOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" onClick={() => setIssueOpen(null)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <form onSubmit={raise} className="glass relative p-6 w-full max-w-md max-h-[90vh] overflow-y-auto space-y-3" onClick={e => e.stopPropagation()}>
            <div className="text-white text-xl font-bold">Raise an Issue</div>
            <input required placeholder="Subject" value={issue.subject} onChange={e => setIssue({...issue, subject:e.target.value})} className="input-base" />
            <textarea required rows={4} placeholder="What's wrong?" value={issue.description} onChange={e => setIssue({...issue, description:e.target.value})} className="input-base" />
            <div className="flex gap-2">
              <button type="button" onClick={() => setIssueOpen(null)} className="btn-ghost flex-1">Cancel</button>
              <button type="submit" className="btn-primary flex-1">Submit</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
